import { useEffect, useState } from 'react'
import { Card, Form, Row, Col, Button, Alert } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { useUpdateAdminUser, useSendPasswordReset } from 'api/hooks/use-admin-user'
import { errorMessage } from 'admin/shared/errors'

const ROLES = [
  { value: 'audit', label: 'Audit' },
  { value: 'non_audit', label: 'Non-audit' }
]

const USER_TYPES = [
  { value: 'standard', label: 'Standard' },
  { value: 'accounting', label: 'Accounting' }
]

const DATUM_ROLES = [
  { value: 'datum_user', label: 'User' },
  { value: 'datum_admin', label: 'Admin' }
]

const buildState = (user) => ({
  first_name: user?.first_name ?? '',
  last_name: user?.last_name ?? '',
  email: user?.email ?? '',
  role: user?.role ?? 'audit',
  user_type: user?.user_type ?? 'standard',
  is_admin: !!user?.is_admin,
  is_user_admin: !!user?.is_user_admin,
  has_analytics_app: !!user?.has_analytics_app,
  has_sod_app: !!user?.has_sod_app,
  has_datum_app: !!user?.has_datum_app,
  datum_role: user?.datum_role ?? '',
  is_beta: !!user?.is_beta
})

const UserInfoForm = ({ user }) => {
  const [form, setForm] = useState(() => buildState(user))
  const [errorMsg, setErrorMsg] = useState(null)

  // Resync the form whenever the server-side user record actually changes —
  // not on every refetch. Keying on `updated_at` (rather than the `user`
  // object reference) prevents background refetches triggered by section
  // toggles from clobbering in-flight edits in this form.
  useEffect(() => {
    setForm(buildState(user))
  }, [user?.updated_at])

  const { mutate: saveUser, isLoading: saving } = useUpdateAdminUser({
    userId: user?.id,
    options: {
      onSuccess: () => {
        setErrorMsg(null)
        toast.success('User updated')
      },
      onError: (err) => {
        const msg = errorMessage(err, 'Failed to update user')
        setErrorMsg(msg)
        toast.error(msg)
      }
    }
  })

  const { mutate: sendPasswordReset, isLoading: sendingReset } = useSendPasswordReset({
    userId: user?.id,
    options: {
      onSuccess: () => toast.success(`Password reset email sent to ${user?.email}`),
      onError: (err) => toast.error(errorMessage(err, 'Failed to send password reset email'))
    }
  })

  const handleSendPasswordReset = () => {
    const confirmed = window.confirm(
      `Send a password reset email to ${user?.email}? They'll receive a link to choose a new password.`
    )
    if (confirmed) sendPasswordReset()
  }

  const setField = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setForm((prev) => {
      const next = { ...prev, [field]: value }
      // If Datum access is being turned off, clear the role so the dropdown
      // doesn't display a stale value while disabled.
      if (field === 'has_datum_app' && !value) next.datum_role = ''
      // If Datum access is being turned on without a role selected, default to
      // the standard "user" role so the form passes the server-side validator.
      if (field === 'has_datum_app' && value && !prev.datum_role) {
        next.datum_role = 'datum_user'
      }
      return next
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const payload = { ...form, datum_role: form.has_datum_app ? form.datum_role : null }
    saveUser(payload)
  }

  return (
    <Card className="mb-4">
      <Card.Header><h5 className="mb-0">User Information</h5></Card.Header>
      <Card.Body>
        {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="user-first-name">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type="text"
                  value={form.first_name}
                  onChange={setField('first_name')}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="user-last-name">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  type="text"
                  value={form.last_name}
                  onChange={setField('last_name')}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="user-email">
                <Form.Label>Email</Form.Label>
                <Form.Control type="email" value={form.email} disabled readOnly />
                <Form.Text className="text-muted">
                  Email is tied to the user&apos;s Auth0 identity and can&apos;t be
                  changed here.
                </Form.Text>
              </Form.Group>
              <Form.Group className="mb-3" controlId="user-role">
                <Form.Label>Role</Form.Label>
                <Form.Select value={form.role} onChange={setField('role')}>
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3" controlId="user-type">
                <Form.Label>User Type</Form.Label>
                <Form.Select value={form.user_type} onChange={setField('user_type')}>
                  {USER_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Accounting users can switch between the municipalities assigned below.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="d-block mb-2">Access</Form.Label>
                <Form.Check
                  type="switch"
                  id="user-is-admin"
                  label="Admin"
                  checked={form.is_admin}
                  onChange={setField('is_admin')}
                />
                <Form.Check
                  type="switch"
                  id="user-is-user-admin"
                  label="User Admin (manages municipality users)"
                  checked={form.is_user_admin}
                  onChange={setField('is_user_admin')}
                />
                <Form.Check
                  type="switch"
                  id="user-has-analytics-app"
                  label="Analytics App"
                  checked={form.has_analytics_app}
                  onChange={setField('has_analytics_app')}
                />
                <Form.Check
                  type="switch"
                  id="user-has-sod-app"
                  label="Access Management App"
                  checked={form.has_sod_app}
                  onChange={setField('has_sod_app')}
                />
                <Form.Check
                  type="switch"
                  id="user-has-datum-app"
                  label="Reports (Datum) App"
                  checked={form.has_datum_app}
                  onChange={setField('has_datum_app')}
                />
                <Form.Check
                  type="switch"
                  id="user-is-beta"
                  label="Beta"
                  checked={form.is_beta}
                  onChange={setField('is_beta')}
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="user-datum-role">
                <Form.Label>Datum Role</Form.Label>
                <Form.Select
                  value={form.datum_role}
                  onChange={setField('datum_role')}
                  disabled={!form.has_datum_app}
                >
                  {!form.has_datum_app && <option value="">—</option>}
                  {DATUM_ROLES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </Form.Select>
                {!form.has_datum_app && (
                  <Form.Text className="text-muted">
                    Enable the Reports (Datum) App to assign a Datum role.
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-between align-items-center">
            <Button
              type="button"
              variant="light"
              className="text-primary"
              onClick={handleSendPasswordReset}
              disabled={sendingReset}
            >
              {sendingReset ? 'Sending…' : 'Send Password Reset Email'}
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  )
}

export default UserInfoForm
